// libraries.
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { monserratSemiBold, robotoRegular, scarpaGray400 } from '@habitech/shared';

// components.
import { CoordinateInputs } from './CoordinateInputs';
import { Line } from '@shared/styles';
import { LoteIdWarning } from '@shared/LoteIdWarning/LoteIdWarning';

// hooks.
import { useGeoreferencingWebSocket } from '../hooks';
import { useGetLeadDetailInfoQuery } from '@domains/lead-detail';

// selectors.
import {
  getSelectedBusinessLine,
  getSelectedNid,
  resetCoordinates,
  updateCoordinates,
} from '../store/slices/georeferencing-slice';
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';

// constants.
import { DROPDOWN_OPTIONS } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/DocumentReview/DocumentForms/ApproveForm/constants';

// styles.
import { WrapperFieldsToCorrect } from './styles';

// Convierte el valor del dropdown CO (string label) al ID numérico para Hermes
const CO_PROPERTY_TYPE_ID_MAP: Record<string, number> = {
  apartamento: 1,
  casa: 2,
  'casa con conjunto cerrado': 3,
};

const getDefaultPropertyTypeValue = (
  country: string | undefined,
  propertyTypeId?: string,
  propertyType?: string
): string => {
  if (country === 'CO') {
    return (propertyType ?? '').toLowerCase().trim();
  }
  return propertyTypeId ?? '';
};

const getPropertyTypeIdForPayload = (
  country: string | undefined,
  selectedValue: string
): number => {
  if (country === 'CO') {
    return CO_PROPERTY_TYPE_ID_MAP[selectedValue] ?? 0;
  }
  return Number(selectedValue) || 0;
};

export const FieldsToCorrect = () => {
  const dispatch = useDispatch();
  const selectedBusinessLine = useSelector(getSelectedBusinessLine);
  const selectedNid = useSelector(getSelectedNid);
  const country = useSelector(getCountry);
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [address, setAddress] = useState('');
  const [buildingComplex, setBuildingComplex] = useState('');
  const [rooms, setRooms] = useState<string>('');
  const [elevators, setElevators] = useState<string>('');

  const { data: leadDetail } = useGetLeadDetailInfoQuery(
    { country, nid: selectedNid || '' },
    { skip: !selectedNid }
  );

  const propertyTypeId = getPropertyTypeIdForPayload(country, selectedPropertyType);

  const isLoadingRef = useRef(false);

  const { sendGeoreferencing, isLoading, canSend } = useGeoreferencingWebSocket({
    onSuccess: () => {
      dispatch(resetCoordinates());
    },
    overrides: {
      address,
      propertyTypeId: propertyTypeId > 0 ? propertyTypeId : undefined,
      buildingComplex: country === 'CO' ? buildingComplex : undefined,
      rooms: country === 'CO' && rooms !== '' ? Number(rooms) : undefined,
      elevators: country === 'CO' && elevators !== '' ? Number(elevators) : undefined,
    },
  });

  // Sincronizar el ref con el estado de loading para usarlo en el useEffect
  isLoadingRef.current = isLoading;

  // Inicializar campos y coordenadas con los valores del lead.
  // Se omite el reset si hay un guardado en curso para no pisar los valores del usuario.
  useEffect(() => {
    if (!leadDetail || isLoadingRef.current) return;

    const defaultValue = getDefaultPropertyTypeValue(
      country,
      leadDetail.property?.property_type_id,
      leadDetail.property?.property_type
    );
    setSelectedPropertyType(defaultValue);
    setAddress(leadDetail.location?.address ?? '');
    setBuildingComplex(leadDetail.location?.house_complex ?? '');
    setRooms(String(leadDetail.property?.rooms ?? ''));
    setElevators(String(leadDetail.property?.elevator ?? ''));

    // Pre-cargar coordenadas existentes en Redux
    const savedCoords = leadDetail.location?.coordinates;
    if (savedCoords?.length === 2) {
      dispatch(updateCoordinates({ lat: savedCoords[0], lng: savedCoords[1] }));
    }
  }, [leadDetail, country]);

  const isDisabled = !canSend || !selectedBusinessLine || isLoading;

  const propertyTypeOptions =
    (country === 'CO'
      ? DROPDOWN_OPTIONS.PROPERTY_TYPE_CO
      : DROPDOWN_OPTIONS.PROPERTY_TYPE) ?? [];

  return (
    <div className="d-flex flex-column h-100">
      <div className="p-3" style={{ backgroundColor: '#f9f9f9' }}>
        <h5
          style={{
            fontFamily: monserratSemiBold,
            fontSize: '16px',
            margin: 0,
          }}
        >
          Campos a corregir
        </h5>
        {selectedNid && (
          <p
            style={{
              fontFamily: robotoRegular,
              fontSize: '13px',
              color: scarpaGray400,
              margin: '4px 0 0 0',
            }}
          >
            NID: {selectedNid}
          </p>
        )}
        {selectedNid && leadDetail && (
          <LoteIdWarning loteId={leadDetail.location?.lote_id ?? null} />
        )}
      </div>
      <div className="p-3 flex-grow-1" style={{ overflowY: 'auto' }}>
        <CoordinateInputs />
        <WrapperFieldsToCorrect className="mt-2">
          <label htmlFor="georef-property-type" className="form-label">
            Tipo de Inmueble
          </label>
          <select
            id="georef-property-type"
            className="form-select mt-1"
            value={selectedPropertyType}
            onChange={e => setSelectedPropertyType(e.target.value)}
          >
            <option value="">Seleccionar</option>
            {propertyTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </WrapperFieldsToCorrect>
        <WrapperFieldsToCorrect className="mt-2">
          <label htmlFor="georef-address" className="form-label">
            Dirección
          </label>
          <input
            id="georef-address"
            type="text"
            className="form-control mt-1"
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="Dirección del inmueble"
          />
        </WrapperFieldsToCorrect>
        {country === 'CO' && (
          <>
            <WrapperFieldsToCorrect className="mt-2">
              <label htmlFor="georef-building-complex" className="form-label">
                Conjunto
              </label>
              <input
                id="georef-building-complex"
                type="text"
                className="form-control mt-1"
                value={buildingComplex}
                onChange={e => setBuildingComplex(e.target.value)}
                placeholder="Nombre del conjunto"
              />
            </WrapperFieldsToCorrect>
            <div className="d-flex gap-2 mt-2">
              <WrapperFieldsToCorrect className="flex-fill">
                <label htmlFor="georef-rooms" className="form-label">
                  Habitaciones
                </label>
                <input
                  id="georef-rooms"
                  type="number"
                  min={0}
                  className="form-control mt-1"
                  value={rooms}
                  onChange={e => setRooms(e.target.value)}
                  placeholder="0"
                />
              </WrapperFieldsToCorrect>
              <WrapperFieldsToCorrect className="flex-fill">
                <label htmlFor="georef-elevators" className="form-label">
                  Elevadores
                </label>
                <input
                  id="georef-elevators"
                  type="number"
                  min={0}
                  className="form-control mt-1"
                  value={elevators}
                  onChange={e => setElevators(e.target.value)}
                  placeholder="0"
                />
              </WrapperFieldsToCorrect>
            </div>
          </>
        )}
      </div>
      <div className="d-flex flex-column gap-3 px-3 pb-3">
        <Line />
        {!selectedBusinessLine && (
          <p
            style={{
              fontFamily: robotoRegular,
              fontSize: '13px',
              color: scarpaGray400,
              margin: 0,
              textAlign: 'center',
            }}
          >
            Selecciona una línea de negocio para continuar
          </p>
        )}
        <div className="d-flex">
          <Button
            className="w-100 btn-habi"
            type="button"
            onClick={sendGeoreferencing}
            disabled={isDisabled}
          >
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
};
